import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  AlertTriangle,
  BriefcaseBusiness,
  CheckCircle2,
  ClipboardList,
  Clock,
  Eye,
  MapPin,
  Plus,
  RefreshCw,
  Send,
  Users,
  Wallet,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@zaogig/auth';
import { Badge } from '@zaogig/ui/badge';
import { Button } from '@zaogig/ui/button';
import { Card, CardContent } from '@zaogig/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@zaogig/ui/dialog';
import { Input } from '@zaogig/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@zaogig/ui/select';
import { Textarea } from '@zaogig/ui/textarea';

// ─── Local types (mirroring @/services/company) ─────
interface CompanyJob {
  id: string;
  title: string;
  status: 'draft' | 'pending_review' | 'published' | 'rejected' | 'offline';
  salary: { min: number; max: number; unit: string };
  tags: string[];
  description?: string;
  requirements?: string;
  benefits?: string;
  location?: { address?: string; city?: string; latitude?: number; longitude?: number };
  createdAt?: string;
}

interface CompanyApplication {
  id: string;
  jobId: string;
  userId: string;
  status: string;
  agStatus?: string;
  companyNote?: string;
  appliedAt: string;
  job?: CompanyJob;
}

interface Employer {
  id: string;
  display_name?: string;
  employer_type?: string;
  status?: string;
}

interface Store {
  id: string;
  store_name: string;
  address: string;
  city?: string;
  district?: string;
  lat?: number;
  lng?: number;
  employer_id?: string;
}

const statusText: Record<string, string> = {
  draft: '草稿',
  pending_review: '待审核',
  published: '招聘中',
  rejected: '已拒绝',
  offline: '已下架',
  submitted: '已报名',
  pending: '待处理',
  viewed: '已查看',
  interviewed: '已面试',
  accepted: '已录用',
  confirmed: '已确认',
  scheduled: '已排班',
  checked_in: '已上岗',
  completed: '已完成',
  cancelled: '已取消',
};

export default function EnterpriseConsole() {
  const [jobs, setJobs] = useState<CompanyJob[]>([]);
  const [applications, setApplications] = useState<CompanyApplication[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateJob, setShowCreateJob] = useState(false);
  const [showJobDetail, setShowJobDetail] = useState<CompanyJob | null>(null);

  const [employers, setEmployers] = useState<Employer[]>([]);
  const [selectedEmployerId, setSelectedEmployerId] = useState('');
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');

  const metrics = useMemo(() => {
    const published = jobs.filter(job => job.status === 'published').length;
    const pendingApplications = applications.filter(item => item.status === 'pending').length;
    const accepted = applications.filter(item => item.status === 'accepted').length;
    const reviewJobs = jobs.filter(job => job.status === 'pending_review').length;
    const settlementEstimate = accepted * 240;
    return { published, pendingApplications, accepted, reviewJobs, settlementEstimate };
  }, [applications, jobs]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [jobsResult, applicationsResult, employersResult] = await Promise.all([
        apiClient.get<{ jobs: CompanyJob[]; total: number }>('/jobs/company/jobs?limit=20'),
        apiClient.get<{ applications: CompanyApplication[]; total: number }>('/jobs/company/applications?limit=20'),
        apiClient.get<Employer[]>('/v1/employers/me').catch(() => [] as Employer[]),
      ]);
      setJobs(jobsResult.jobs);
      setApplications(applicationsResult.applications);
      setEmployers(employersResult || []);
      if (employersResult?.length > 0 && !selectedEmployerId) {
        const firstId = employersResult[0]!;
        setSelectedEmployerId(firstId.id);
        apiClient.get<Store[]>(`/v1/employers/${firstId}/stores`).then(setStores).catch(() => {});
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '企业工作台加载失败');
    } finally {
      setLoading(false);
    }
  }, [selectedEmployerId]);

  const handleEmployerChange = async (employerId: string) => {
    setSelectedEmployerId(employerId);
    setSelectedStoreId('');
    try {
      const storeList = await apiClient.get<Store[]>(`/v1/employers/${employerId}/stores`);
      setStores(storeList);
    } catch {
      setStores([]);
    }
  };

  const handlePublish = async (jobId: string) => {
    try {
      await apiClient.post<CompanyJob>(`/jobs/${jobId}/publish`);
      toast.success('岗位已发布');
      loadData();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '发布失败');
    }
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleApplication = async (applicationId: string, status: 'accepted' | 'rejected') => {
    try {
      const updated = await apiClient.put<{ application: CompanyApplication }>(
        `/jobs/company/applications/${applicationId}/status`,
        { status }
      );
      setApplications(items => items.map(item => item.id === applicationId ? updated.application : item));
      toast.success(status === 'accepted' ? '已录用报名人员' : '已拒绝报名');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '报名处理失败');
    }
  };

  return (
    <div className="wx-page bg-gray-50">
      <div className="border-b border-gray-100 bg-white px-4 pt-8 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">企业端</p>
            <h1 className="text-xl font-bold text-gray-900">用工管理工作台</h1>
          </div>
          <Button variant="outline" size="icon" onClick={loadData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 px-4 pt-4">
        <MetricCard icon={<BriefcaseBusiness className="h-5 w-5" />} label="招聘中岗位" value={metrics.published} tone="blue" />
        <MetricCard icon={<Users className="h-5 w-5" />} label="待处理报名" value={metrics.pendingApplications} tone="orange" />
        <MetricCard icon={<Clock className="h-5 w-5" />} label="待审核岗位" value={metrics.reviewJobs} tone="gray" />
        <MetricCard icon={<Wallet className="h-5 w-5" />} label="待结算预估" value={`¥${metrics.settlementEstimate}`} tone="green" />
      </div>

      <div className="px-4 pt-4">
        <Button className="wx-btn-primary w-full" onClick={() => setShowCreateJob(true)}>
          <Plus className="mr-2 h-4 w-4" />
          发布任务
        </Button>
      </div>

      <section className="px-4 pt-5">
        <SectionTitle icon={<ClipboardList className="h-4 w-4" />} title="岗位/任务" />
        <div className="space-y-3">
          {jobs.length === 0 ? (
            <EmptyBlock text="暂无企业岗位。发布任务后会出现在这里。" />
          ) : jobs.map(job => (
            <Card key={job.id} className="wx-card">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{job.title}</h3>
                    <p className="mt-1 text-sm text-gray-500">{job.location?.address || '未填写地点'}</p>
                  </div>
                  <Badge className={job.status === 'published' ? 'wx-badge-green' : 'wx-badge-orange'}>
                    {statusText[job.status] || job.status}
                  </Badge>
                </div>
                <div className="mt-3 flex items-center justify-between text-sm">
                  <span className="text-gray-500">{job.tags.slice(0, 3).join(' / ') || '未设置标签'}</span>
                  <span className="font-semibold text-orange-500">
                    ¥{job.salary.min}-{job.salary.max}/{job.salary.unit === 'day' ? '天' : '时'}
                  </span>
                </div>
                <div className="mt-3 flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowJobDetail(job)}>
                    <Eye className="mr-1 h-3 w-3" />
                    详情
                  </Button>
                  {job.status !== 'published' && job.status !== 'offline' && (
                    <Button size="sm" className="wx-btn-primary" onClick={() => handlePublish(job.id)}>
                      <Send className="mr-1 h-3 w-3" />
                      发布
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="px-4 pt-5 pb-8">
        <SectionTitle icon={<Users className="h-4 w-4" />} title="报名人员" />
        <div className="space-y-3">
          {applications.length === 0 ? (
            <EmptyBlock text="暂无报名记录。岗位发布并审核后，候选人会进入这里。" />
          ) : applications.map(application => (
            <Card key={application.id} className="wx-card">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{application.job?.title || application.jobId}</h3>
                    <p className="mt-1 text-sm text-gray-500">用户 {application.userId}</p>
                  </div>
                  <Badge className={application.status === 'accepted' ? 'wx-badge-green' : 'wx-badge-blue'}>
                    {statusText[application.status] || application.status}
                  </Badge>
                </div>
                {(application.status === 'submitted' || application.status === 'pending') && (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <Button variant="outline" onClick={() => handleApplication(application.id, 'rejected')}>
                      <XCircle className="mr-2 h-4 w-4" />
                      拒绝
                    </Button>
                    <Button className="wx-btn-primary" onClick={() => handleApplication(application.id, 'accepted')}>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      录用
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <CreateJobDialog
        open={showCreateJob}
        onOpenChange={setShowCreateJob}
        onCreated={(job) => {
          setJobs(items => [job, ...items]);
          setShowCreateJob(false);
        }}
        employers={employers}
        stores={stores}
        selectedEmployerId={selectedEmployerId}
        selectedStoreId={selectedStoreId}
        onEmployerChange={handleEmployerChange}
      />

      <JobDetailDialog
        job={showJobDetail}
        open={!!showJobDetail}
        onClose={() => setShowJobDetail(null)}
      />
    </div>
  );
}

function MetricCard({ icon, label, value, tone }: { icon: React.ReactNode; label: string; value: string | number; tone: 'blue' | 'orange' | 'green' | 'gray' }) {
  const toneClass = {
    blue: 'bg-blue-50 text-blue-600',
    orange: 'bg-orange-50 text-orange-600',
    green: 'bg-green-50 text-green-600',
    gray: 'bg-gray-100 text-gray-600',
  }[tone];

  return (
    <Card className="wx-card">
      <CardContent className="p-4">
        <div className={`mb-3 flex h-9 w-9 items-center justify-center rounded-lg ${toneClass}`}>
          {icon}
        </div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="mt-1 text-xs text-gray-500">{label}</p>
      </CardContent>
    </Card>
  );
}

function SectionTitle({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="mb-3 flex items-center gap-2 text-gray-900">
      {icon}
      <h2 className="font-semibold">{title}</h2>
    </div>
  );
}

function EmptyBlock({ text }: { text: string }) {
  return (
    <Card className="wx-card border-dashed">
      <CardContent className="flex items-center gap-2 p-4 text-sm text-gray-500">
        <AlertTriangle className="h-4 w-4" />
        {text}
      </CardContent>
    </Card>
  );
}

function CreateJobDialog({ open, onOpenChange, onCreated, employers, stores, selectedEmployerId, selectedStoreId, onEmployerChange }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (job: CompanyJob) => void;
  employers: Employer[];
  stores: Store[];
  selectedEmployerId: string;
  selectedStoreId: string;
  onEmployerChange: (id: string) => void;
}) {
  const [title, setTitle] = useState('');
  const [address, setAddress] = useState('');
  const [salaryMin, setSalaryMin] = useState('180');
  const [salaryMax, setSalaryMax] = useState('240');
  const [tags, setTags] = useState('临时工,日结');
  const [description, setDescription] = useState('');
  const [storeId, setStoreId] = useState(selectedStoreId);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setStoreId(selectedStoreId); }, [selectedStoreId]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const job = await apiClient.post<CompanyJob>('/jobs', {
        title,
        salary: { min: Number(salaryMin), max: Number(salaryMax), unit: 'day' },
        tags: tags.split(',').map((tag: string) => tag.trim()).filter(Boolean),
        description,
        requirements: description,
        location: { address },
        employer_id: selectedEmployerId || undefined,
        store_id: storeId || undefined,
      });
      toast.success('任务已提交审核');
      onCreated(job);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '发布任务失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>发布任务</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {employers.length > 0 && (
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">用工主体</label>
              <Select value={selectedEmployerId} onValueChange={onEmployerChange}>
                <SelectTrigger><SelectValue placeholder="选择用工主体" /></SelectTrigger>
                <SelectContent>
                  {employers.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.display_name || e.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {stores.length > 0 && (
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">门店</label>
              <Select value={storeId} onValueChange={setStoreId}>
                <SelectTrigger><SelectValue placeholder="选择门店" /></SelectTrigger>
                <SelectContent>
                  {stores.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.store_name} - {s.address}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {stores.length === 0 && employers.length > 0 && (
            <p className="text-xs text-amber-600">该用工主体暂无门店，岗位仍可创建但缺少门店关联</p>
          )}
          <Input value={title} onChange={event => setTitle(event.target.value)} placeholder="任务名称" />
          <Input value={address} onChange={event => setAddress(event.target.value)} placeholder="工作地点" />
          <div className="grid grid-cols-2 gap-2">
            <Input value={salaryMin} onChange={event => setSalaryMin(event.target.value)} placeholder="最低日薪" />
            <Input value={salaryMax} onChange={event => setSalaryMax(event.target.value)} placeholder="最高日薪" />
          </div>
          <Input value={tags} onChange={event => setTags(event.target.value)} placeholder="标签，用英文逗号分隔" />
          <Textarea value={description} onChange={event => setDescription(event.target.value)} placeholder="任务要求" />
          <Button className="wx-btn-primary w-full" onClick={submit} disabled={submitting || !title || !address}>
            {submitting ? '提交中' : '提交审核'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function JobDetailDialog({ job, open, onClose }: { job: CompanyJob | null; open: boolean; onClose: () => void }) {
  if (!job) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{job.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex gap-2 items-center">
            <Badge className={job.status === 'published' ? 'wx-badge-green' : 'wx-badge-orange'}>
              {statusText[job.status] || job.status}
            </Badge>
          </div>
          <div className="flex items-center gap-1 text-gray-600">
            <MapPin className="h-4 w-4" /> {job.location?.address || '未填写地点'}
          </div>
          <div className="font-semibold text-orange-500">
            ¥{job.salary.min}-{job.salary.max}/{job.salary.unit === 'day' ? '天' : '时'}
          </div>
          {job.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {job.tags.map(t => <Badge key={t} variant="outline">{t}</Badge>)}
            </div>
          )}
          {job.description && <p className="text-gray-700">{job.description}</p>}
          {job.requirements && <p className="text-gray-500 text-xs">要求: {job.requirements}</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
